// src/screens/DeveloperDashboardScreen.js
// ============================================================================
// DEVELOPER DASHBOARD - Game developer management portal
//
// Tabs: Overview | Games | Analytics | API & Keys
//
// Access:
// - Developer accounts: users listed in developers/{id}.memberUids
// - Admin fallback: if user is admin, reads all developer accounts from Firestore
//
// LEGAL COMPLIANCE: Uses custodial terminology per CUSTODIAL_TERMINOLOGY_GUIDE.md
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, RefreshControl,
  Modal, TextInput, Dimensions, Platform
} from 'react-native';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getFirestore, collection, query, where, getDocs,
  doc, getDoc, orderBy, limit, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { auth } from '../../firebaseConfig';

import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const functions = getFunctions();
const db = getFirestore();

// ============================================================================
// CONSTANTS
// ============================================================================
// POST-PIVOT (2026-06-27): the 3rd-party real-money competition-integration path
// (API keys, competition volume/revenue, revenue-share tiers) is PAUSED — devs
// want cash, not Crit revenue share. Hide those surfaces; keep game submission /
// "sell your game". Flip to true to revive. Mirrors DeveloperPortalScreen.
const SHOW_COMPETITION_INTEGRATION = false;

const TABS = SHOW_COMPETITION_INTEGRATION
  ? ['Overview', 'Games', 'Analytics', 'API & Keys']
  : ['Overview', 'Games'];

const INTEGRATION_LEVEL_INFO = {
  0: { name: 'Stream-Based', icon: 'eye', color: '#9146FF', description: 'Results detected automatically from stream' },
  1: { name: 'Result Reporting', icon: 'cloud-upload', color: '#3498db', description: 'Game server reports match outcomes' },
  2: { name: 'Event Management', icon: 'trophy', color: '#FCD000', description: 'Create tournaments from your game' },
  3: { name: 'Full Integration', icon: 'medal', color: '#2ECC71', description: 'In-game wallet and competition UI' },
};

const GAME_STATUS_COLORS = {
  active: '#2ECC71',
  review: '#f39c12',
  draft: '#666',
  suspended: '#e74c3c',
};

const REVENUE_TIERS = [
  { min: 0, max: 50000, devShare: 10, label: 'Starter' },
  { min: 50000, max: 250000, devShare: 15, label: 'Growth' },
  { min: 250000, max: 1000000, devShare: 18, label: 'Partner' },
  { min: 1000000, max: Infinity, devShare: 20, label: 'Premier' },
];

// ============================================================================
// HELPERS
// ============================================================================
function formatCents(cents) {
  if (cents == null) return '$0.00';
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNumber(n) {
  if (n == null) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString();
}

function formatDate(ts) {
  if (!ts) return '--';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getRevenueTier(monthlyVolumeCents) {
  const dollars = (monthlyVolumeCents || 0) / 100;
  for (const tier of REVENUE_TIERS) {
    if (dollars >= tier.min && dollars < tier.max) return tier;
  }
  return REVENUE_TIERS[0];
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function DeveloperDashboardScreen({ navigation }) {
  const { showError } = useError();

  // Auth & role
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Developer data
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState('Overview');
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [showNewGameModal, setShowNewGameModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Submissions & applications
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [developerApplications, setDeveloperApplications] = useState([]);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showApiKeyRevealModal, setShowApiKeyRevealModal] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState(null);
  const [processingAppId, setProcessingAppId] = useState(null);
  const [activeRejectAppId, setActiveRejectAppId] = useState(null);

  // New game form
  const [newGameForm, setNewGameForm] = useState({
    name: '',
    description: '',
    genres: '',
    platforms: '',
    coverImageUrl: '',
    trailerUrl: '',
    externalStoreUrl: '',
    downloadUrlWin: '',
    downloadUrlMac: '',
    integrationLevel: 0,
    competitionModes: 'h2h',
    minPlayers: '2',
    maxPlayers: '2',
    streamRequired: true,
    developerNotes: '',
  });

  // ============================================================================
  // AUTH + INITIAL LOAD
  // ============================================================================
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        await checkAdminAndLoad(u);
      } else {
        setLoading(false);
      }
    });
    return unsubAuth;
  }, []);

  const checkAdminAndLoad = async (u) => {
    try {
      // Check admin role
      const userDoc = await getDoc(doc(db, 'users', u.uid));
      const admin = userDoc.exists() && userDoc.data().role === 'admin';
      setIsAdmin(admin);
      await loadDeveloperAccounts(u, admin);
    } catch (err) {
      console.error('Error checking admin status:', err);
      await loadDeveloperAccounts(u, false);
    }
  };

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  const loadDeveloperAccounts = async (u, admin) => {
    try {
      let accts = [];

      // Try cloud function first
      try {
        const getMyDevAccounts = httpsCallable(functions, 'getMyDeveloperAccounts');
        const result = await getMyDevAccounts();
        accts = result.data?.accounts || result.data || [];
      } catch (fnErr) {
        console.log('Cloud function unavailable, trying Firestore:', fnErr.message);
      }

      // Admin fallback: read directly from Firestore
      if (accts.length === 0 && admin) {
        try {
          const snap = await getDocs(collection(db, 'developers'));
          accts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (fsErr) {
          console.log('Firestore fallback failed:', fsErr.message);
        }
      }

      // Non-admin fallback: query by memberUids
      if (accts.length === 0 && !admin && u) {
        try {
          const q = query(
            collection(db, 'developers'),
            where('memberUids', 'array-contains', u.uid)
          );
          const snap = await getDocs(q);
          accts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (fsErr) {
          console.log('Member query failed:', fsErr.message);
        }
      }

      setAccounts(accts);
      if (accts.length > 0) {
        setSelectedAccount(accts[0]);
        await loadAccountData(accts[0]);
      } else if (admin) {
        // Admin with no developer account — still load pending applications
        await loadPendingApplications();
      }
    } catch (err) {
      console.error('Error loading developer accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountData = async (account) => {
    if (!account) return;
    await Promise.all([
      loadGames(account.id),
      loadAnalytics(account.id),
      isAdmin ? loadPendingApplications() : Promise.resolve(),
    ]);
  };

  const loadPendingApplications = async () => {
    try {
      // Try cloud function first
      try {
        const getPending = httpsCallable(functions, 'adminGetPendingGameReviews');
        const result = await getPending();
        console.log('adminGetPendingGameReviews result:', JSON.stringify(result.data));
        if (result.data) {
          if (result.data.applications) {
            setDeveloperApplications(result.data.applications);
          }
          if (result.data.submissions) {
            setPendingSubmissions(result.data.submissions);
          }
        }
        return;
      } catch (fnErr) {
        console.warn('adminGetPendingGameReviews unavailable, trying Firestore:', fnErr.code, fnErr.message);
      }

      // Firestore fallback — try with orderBy first, then simple query
      let apps = [];
      try {
        const q = query(
          collection(db, 'developerApplications'),
          where('status', '==', 'new'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        apps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (fsErr) {
        console.warn('Applications ordered query failed:', fsErr.code, fsErr.message);
        // Simple fallback without orderBy
        try {
          const simpleQ = query(
            collection(db, 'developerApplications'),
            where('status', '==', 'new')
          );
          const snap = await getDocs(simpleQ);
          apps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          apps.sort((a, b) => {
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
            return bTime - aTime;
          });
        } catch (err2) {
          console.warn('Simple applications query also failed:', err2.code, err2.message);
        }
      }
      setDeveloperApplications(apps);

      // Also try to load pending game submissions via Firestore
      try {
        const subQ = query(
          collection(db, 'gameSubmissions'),
          where('status', 'in', ['submitted', 'in_review'])
        );
        const subSnap = await getDocs(subQ);
        setPendingSubmissions(subSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (subErr) {
        console.warn('Game submissions query failed:', subErr.code, subErr.message);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  const loadGames = async (developerId) => {
    try {
      let gameList = [];

      // Try cloud function
      try {
        const getDeveloperGames = httpsCallable(functions, 'getDeveloperGames');
        const result = await getDeveloperGames({ developerId });
        gameList = result.data?.games || result.data || [];
        // Also capture pending submissions from the response
        if (result.data?.pendingSubmissions) {
          setPendingSubmissions(result.data.pendingSubmissions);
        }
      } catch (fnErr) {
        console.log('getDeveloperGames unavailable, trying Firestore:', fnErr.message);
      }

      // Firestore fallback
      if (gameList.length === 0) {
        try {
          // Try with orderBy first (requires composite index)
          const q = query(
            collection(db, 'dev_games'),
            where('developerId', '==', developerId),
            orderBy('createdAt', 'desc')
          );
          const snap = await getDocs(q);
          gameList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (fsErr) {
          console.log('Ordered query failed (missing index?), trying simple query:', fsErr.message);
          // Fallback: query without orderBy, sort client-side
          try {
            const simpleQ = query(
              collection(db, 'dev_games'),
              where('developerId', '==', developerId)
            );
            const snap = await getDocs(simpleQ);
            gameList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            gameList.sort((a, b) => {
              const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
              const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
              return bTime - aTime;
            });
          } catch (simpleErr) {
            console.log('Simple Firestore games query also failed:', simpleErr.message);
          }
        }
      }

      setGames(gameList);
      if (gameList.length > 0 && !selectedGame) {
        setSelectedGame(gameList[0]);
      }
    } catch (err) {
      console.error('Error loading games:', err);
    }
  };

  const loadAnalytics = async (developerId) => {
    try {
      // Try cloud function
      try {
        const getDevAnalytics = httpsCallable(functions, 'getDeveloperAnalytics');
        const result = await getDevAnalytics({ developerId });
        if (result.data) {
          setAnalytics(result.data);
          setDailyStats(result.data.daily || []);
          return;
        }
      } catch (fnErr) {
        console.log('getDeveloperAnalytics unavailable, trying Firestore:', fnErr.message);
      }

      // Firestore fallback - read daily stats
      try {
        let daily = [];
        try {
          // Try with orderBy (requires composite index)
          const q = query(
            collection(db, 'dev_daily_stats'),
            where('developerId', '==', developerId),
            orderBy('date', 'desc'),
            limit(30)
          );
          const snap = await getDocs(q);
          daily = snap.docs.map(d => d.data()).reverse();
        } catch (indexErr) {
          console.log('Ordered stats query failed (missing index?), trying simple query:', indexErr.message);
          // Fallback: query without orderBy, sort client-side
          try {
            const simpleQ = query(
              collection(db, 'dev_daily_stats'),
              where('developerId', '==', developerId)
            );
            const snap = await getDocs(simpleQ);
            daily = snap.docs.map(d => d.data());
            daily.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
            // Take last 30 entries
            if (daily.length > 30) daily = daily.slice(-30);
          } catch (simpleErr) {
            console.log('Simple Firestore stats query also failed:', simpleErr.message);
          }
        }
        
        if (daily.length > 0) {
          setDailyStats(daily);

          // Compute totals from daily stats
          const totals = daily.reduce((acc, day) => ({
            totalCompetitionVolumeCents: acc.totalCompetitionVolumeCents + (day.competitionVolumeCents || 0),
            totalRevenueCents: acc.totalRevenueCents + (day.revenueEarnedCents || 0),
            totalMatches: acc.totalMatches + (day.matches || 0),
            totalUniquePlayers: acc.totalUniquePlayers + (day.uniquePlayers || 0),
          }), { totalCompetitionVolumeCents: 0, totalRevenueCents: 0, totalMatches: 0, totalUniquePlayers: 0 });

          setAnalytics(totals);
        }
      } catch (fsErr) {
        console.log('Firestore analytics fallback failed:', fsErr.message);
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    if (selectedAccount) {
      await loadAccountData(selectedAccount);
    } else if (isAdmin) {
      // Admin with no developer account — refresh pending applications
      await loadPendingApplications();
    }
    setRefreshing(false);
  }, [selectedAccount, isAdmin]);

  // ============================================================================
  // ACTIONS
  // ============================================================================
  const handleSwitchAccount = (account) => {
    setSelectedAccount(account);
    setSelectedGame(null);
    setShowAccountSwitcher(false);
    loadAccountData(account);
  };

  const handleRegisterGame = async () => {
    if (!newGameForm.name) {
      showError('Missing Info', 'Please enter a game name.');
      return;
    }
    setSubmitting(true);
    try {
      const submitGame = httpsCallable(functions, 'submitGameForReview');
      await submitGame({
        developerId: selectedAccount.id,
        name: newGameForm.name,
        description: newGameForm.description,
        genres: newGameForm.genres ? newGameForm.genres.split(',').map(g => g.trim()).filter(Boolean) : [],
        platforms: newGameForm.platforms ? newGameForm.platforms.split(',').map(p => p.trim()).filter(Boolean) : [],
        coverImageUrl: newGameForm.coverImageUrl || null,
        trailerUrl: newGameForm.trailerUrl || null,
        externalStoreUrl: newGameForm.externalStoreUrl || null,
        downloadUrls: {
          win32: newGameForm.downloadUrlWin || null,
          darwin: newGameForm.downloadUrlMac || null,
        },
        integrationLevel: newGameForm.integrationLevel,
        competitionConfig: {
          allowedModes: newGameForm.competitionModes ? newGameForm.competitionModes.split(',').map(m => m.trim()).filter(Boolean) : ['h2h'],
          teamSizes: [1],
          minPlayers: parseInt(newGameForm.minPlayers) || 2,
          maxPlayers: parseInt(newGameForm.maxPlayers) || 2,
          streamRequired: newGameForm.streamRequired,
        },
        developerNotes: newGameForm.developerNotes || '',
      });
      setShowNewGameModal(false);
      setNewGameForm({
        name: '', description: '', genres: '', platforms: '', coverImageUrl: '',
        trailerUrl: '', externalStoreUrl: '', downloadUrlWin: '', downloadUrlMac: '',
        integrationLevel: 0, competitionModes: 'h2h', minPlayers: '2', maxPlayers: '2',
        streamRequired: true, developerNotes: '',
      });
      await loadGames(selectedAccount.id);
      showError('Game Submitted', 'Your game has been submitted for review. You will be notified when it is approved.');
    } catch (err) {
      console.error('Error submitting game:', err);
      showError('Error', 'Failed to submit game. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRotateApiKey = async () => {
    if (!selectedGame) {
      showError('Select Game', 'Please select a game first.');
      return;
    }
    try {
      const rotateKey = httpsCallable(functions, 'rotateDeveloperApiKey');
      const result = await rotateKey({ gameId: selectedGame.gameId || selectedGame.id });
      setApiKeyRevealed(false);
      if (result.data?.apiKey) {
        setRevealedKeys({ apiKey: result.data.apiKey, secretKey: result.data.secretKey });
        setShowApiKeyRevealModal(true);
      } else {
        showError('API Key Rotated', 'Your new API key is active. The old key is now invalid.');
      }
      await loadDeveloperAccounts(user, isAdmin);
    } catch (err) {
      showError('Error', 'Failed to rotate API key.');
    }
  };

  const handleApproveApplication = async (app) => {
    setProcessingAppId(app.id);
    try {
      const createAccount = httpsCallable(functions, 'adminCreateDeveloperAccount');
      await createAccount({
        applicationId: app.id,
        studioName: app.studioName,
        contactEmail: app.email,
        contactName: app.studioName,
        website: app.website || '',
        teamSize: app.teamSize || '',
        ownerUid: app.userId || null,
      });
      showError('Account Created', `Developer account created for ${app.studioName}. They've been sent a welcome email.`);
      // Reload applications
      await loadPendingApplications();
      await loadDeveloperAccounts(user, isAdmin);
    } catch (err) {
      console.error('Error creating developer account:', err);
      showError('Error', err.message || 'Failed to create developer account.');
    } finally {
      setProcessingAppId(null);
    }
  };

  const handleRejectApplication = async (app) => {
    if (!rejectionReason.trim()) {
      showError('Required', 'Please enter a rejection reason.');
      return;
    }
    setProcessingAppId(app.id);
    try {
      const reviewApp = httpsCallable(functions, 'adminCreateDeveloperAccount');
      await reviewApp({
        action: 'reject',
        applicationId: app.id,
        reason: rejectionReason.trim(),
      });
      showError('Application Rejected', `Application from ${app.studioName} has been rejected. They've been notified.`);
      setRejectionReason('');
      setActiveRejectAppId(null);
      await loadPendingApplications();
    } catch (err) {
      console.error('Reject application error:', err.code, err.message, err);
      showError('Error', err.message || 'Failed to reject application.');
    } finally {
      setProcessingAppId(null);
    }
  };

  // ============================================================================
  // RENDER: LOADING / NO AUTH / NO ACCOUNT
  // ============================================================================
  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.centerContent}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={styles.loadingText}>Loading Developer Dashboard...</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  if (!user) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.centerContent}>
          <Ionicons name="lock-closed" size={48} color="#666" />
          <Text style={styles.emptyTitle}>Sign In Required</Text>
          <Text style={styles.emptyText}>Please sign in to access the Developer Dashboard.</Text>
          <TouchableOpacity style={styles.goldButton} onPress={() => navigation.navigate('Login')}>
            <Text style={styles.goldButtonText}>Sign In</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  if (!selectedAccount && !isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.centerContent}>
          <Ionicons name="code-slash" size={48} color="#666" />
          <Text style={styles.emptyTitle}>No Developer Account</Text>
          <Text style={styles.emptyText}>
            You don't have a developer account yet. Apply through the Developer Portal to get started.
          </Text>
          <TouchableOpacity
            style={styles.goldButton}
            onPress={() => navigation.navigate('DeveloperPortal')}
          >
            <Text style={styles.goldButtonText}>Developer Portal</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  // Admin with no developer account — show admin-only review panel
  if (!selectedAccount && isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />

        {/* Admin Header */}
        <View style={styles.dashboardHeader}>
          <View style={styles.dashboardHeaderLeft}>
            <View style={styles.studioIcon}>
              <Ionicons name="shield-checkmark" size={24} color="#FCD000" />
            </View>
            <View>
              <View style={styles.dashboardTitleRow}>
                <Text style={styles.dashboardTitle}>Developer Reviews</Text>
                <View style={styles.adminBadge}>
                  <Text style={styles.adminBadgeText}>ADMIN</Text>
                </View>
              </View>
              <Text style={styles.dashboardSubtitle}>Review applications & game submissions</Text>
            </View>
          </View>
        </View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#FCD000" />}
        >
          {renderAdminPendingSection()}

          {developerApplications.length === 0 && pendingSubmissions.length === 0 && (
            <View style={styles.card}>
              <View style={{ alignItems: 'center', paddingVertical: 24 }}>
                <Ionicons name="checkmark-circle" size={48} color="#2ECC71" />
                <Text style={[styles.emptyTitle, { marginTop: 12 }]}>All Caught Up</Text>
                <Text style={styles.emptyText}>
                  No pending developer applications or game submissions to review.
                </Text>
              </View>
            </View>
          )}
        </ScrollView>

        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const monthlyVolumeCents = analytics?.totalCompetitionVolumeCents ||
    (games.length > 0 ? games.reduce((sum, g) => sum + (g.totalVolumeCents || 0), 0) : 0);
  const currentTier = getRevenueTier(monthlyVolumeCents);
  const totalRevenue = analytics?.totalRevenueCents || selectedAccount?.totalRevenueCents ||
    (games.length > 0 ? games.reduce((sum, g) => sum + (g.revenueEarnedCents || 0), 0) : 0);
  const totalMatches = analytics?.totalMatches || selectedAccount?.totalMatchesFacilitated ||
    (games.length > 0 ? games.reduce((sum, g) => sum + (g.totalMatches || 0), 0) : 0);
  const activeGames = games.filter(g => g.status === 'active').length;

  // ============================================================================
  // RENDER: DASHBOARD
  // ============================================================================
  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />

      {/* Dashboard Header */}
      <TouchableOpacity
        style={styles.dashboardHeader}
        onPress={() => accounts.length > 1 && setShowAccountSwitcher(true)}
        activeOpacity={accounts.length > 1 ? 0.7 : 1}
      >
        <View style={styles.dashboardHeaderLeft}>
          <View style={styles.studioIcon}>
            <Ionicons name="code-slash" size={24} color="#FCD000" />
          </View>
          <View>
            <View style={styles.dashboardTitleRow}>
              <Text style={styles.dashboardTitle}>{selectedAccount.studioName || 'Developer'}</Text>
              {isAdmin && (
                <View style={styles.adminBadge}>
                  <Text style={styles.adminBadgeText}>ADMIN</Text>
                </View>
              )}
            </View>
            <Text style={styles.dashboardSubtitle}>
              {currentTier.label} Tier  {accounts.length > 1 ? ' - Tap to switch' : ''}
            </Text>
          </View>
        </View>
        {accounts.length > 1 && (
          <Ionicons name="chevron-down" size={20} color="#666" />
        )}
      </TouchableOpacity>

      {/* Tabs */}
      <View style={styles.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabScroll}>
          {TABS.map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[styles.tab, activeTab === tab && styles.tabActive]}
              onPress={() => setActiveTab(tab)}
            >
              <Text style={[styles.tabText, activeTab === tab && styles.tabTextActive]}>{tab}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tab Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#FCD000" />}
      >
        {activeTab === 'Overview' && renderOverviewTab()}
        {activeTab === 'Games' && renderGamesTab()}
        {activeTab === 'Analytics' && renderAnalyticsTab()}
        {activeTab === 'API & Keys' && renderApiTab()}
      </ScrollView>

      {/* Modals */}
      {renderAccountSwitcherModal()}
      {renderNewGameModal()}
      {renderApiKeyRevealModal()}

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );

  // ============================================================================
  // TAB: OVERVIEW
  // ============================================================================
  function renderOverviewTab() {
    return (
      <View>
        {/* KPI Cards */}
        <View style={styles.kpiGrid}>
          <View style={styles.kpiCard}>
            <Ionicons name="game-controller" size={20} color="#3498db" />
            <Text style={styles.kpiValue}>{activeGames}</Text>
            <Text style={styles.kpiLabel}>Active Games</Text>
          </View>
          <View style={styles.kpiCard}>
            <Ionicons name="trophy" size={20} color="#FCD000" />
            <Text style={styles.kpiValue}>{formatNumber(totalMatches)}</Text>
            <Text style={styles.kpiLabel}>Total Matches</Text>
          </View>
          {SHOW_COMPETITION_INTEGRATION && (
            <>
              <View style={styles.kpiCard}>
                <Ionicons name="cash" size={20} color="#2ECC71" />
                <Text style={styles.kpiValue}>{formatCents(totalRevenue)}</Text>
                <Text style={styles.kpiLabel}>Revenue Earned</Text>
              </View>
              <View style={styles.kpiCard}>
                <Ionicons name="trending-up" size={20} color="#9146FF" />
                <Text style={styles.kpiValue}>{formatCents(monthlyVolumeCents)}</Text>
                <Text style={styles.kpiLabel}>Competition Volume</Text>
              </View>
            </>
          )}
        </View>

        {/* Account Details */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Account Details</Text>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Studio</Text>
            <Text style={styles.detailValue}>{selectedAccount.studioName || '--'}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Contact</Text>
            <Text style={styles.detailValue}>{selectedAccount.contactName || '--'}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Email</Text>
            <Text style={styles.detailValue}>{selectedAccount.contactEmail || '--'}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Status</Text>
            <View style={[styles.statusBadge, { backgroundColor: selectedAccount.status === 'active' ? '#2ECC7122' : '#f39c1222' }]}>
              <Text style={[styles.statusBadgeText, { color: selectedAccount.status === 'active' ? '#2ECC71' : '#f39c12' }]}>
                {(selectedAccount.status || 'active').toUpperCase()}
              </Text>
            </View>
          </View>
          {SHOW_COMPETITION_INTEGRATION && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Revenue Tier</Text>
              <Text style={[styles.detailValue, { color: '#FCD000' }]}>
                {currentTier.label} ({currentTier.devShare}% dev share)
              </Text>
            </View>
          )}
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Member Since</Text>
            <Text style={styles.detailValue}>{formatDate(selectedAccount.createdAt)}</Text>
          </View>
        </View>

        {/* Quick Actions */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Quick Actions</Text>
          <View style={styles.quickActionsGrid}>
            <TouchableOpacity style={styles.quickAction} onPress={() => setShowNewGameModal(true)}>
              <Ionicons name="add-circle" size={24} color="#FCD000" />
              <Text style={styles.quickActionText}>Register Game</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.quickAction} onPress={() => setActiveTab('Games')}>
              <Ionicons name="game-controller" size={24} color="#3498db" />
              <Text style={styles.quickActionText}>View Games</Text>
            </TouchableOpacity>
            {SHOW_COMPETITION_INTEGRATION && (
              <>
                <TouchableOpacity style={styles.quickAction} onPress={() => setActiveTab('Analytics')}>
                  <Ionicons name="bar-chart" size={24} color="#9146FF" />
                  <Text style={styles.quickActionText}>Analytics</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.quickAction} onPress={() => setActiveTab('API & Keys')}>
                  <Ionicons name="key" size={24} color="#2ECC71" />
                  <Text style={styles.quickActionText}>API Keys</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>

        {/* Recent Games */}
        {games.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Your Games</Text>
            {games.slice(0, 3).map((game) => renderGameRow(game))}
            {games.length > 3 && (
              <TouchableOpacity style={styles.seeAllButton} onPress={() => setActiveTab('Games')}>
                <Text style={styles.seeAllText}>See All Games ({games.length})</Text>
                <Ionicons name="arrow-forward" size={16} color="#FCD000" />
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Admin: Pending Reviews */}
        {isAdmin && renderAdminPendingSection()}
      </View>
    );
  }

  // ============================================================================
  // TAB: GAMES
  // ============================================================================
  function renderGamesTab() {
    return (
      <View>
        {/* Header row */}
        <View style={styles.tabHeaderRow}>
          <Text style={styles.tabHeaderTitle}>Your Games ({games.length})</Text>
          <TouchableOpacity style={styles.addButton} onPress={() => setShowNewGameModal(true)}>
            <Ionicons name="add" size={18} color="#000" />
            <Text style={styles.addButtonText}>Submit Game</Text>
          </TouchableOpacity>
        </View>

        {/* Pending Submissions */}
        {pendingSubmissions.length > 0 && (
          <View style={{ marginBottom: 16 }}>
            <Text style={[styles.tabHeaderTitle, { fontSize: 15, marginBottom: 8 }]}>
              Pending Submissions ({pendingSubmissions.length})
            </Text>
            {pendingSubmissions.map((sub) => renderSubmissionCard(sub))}
          </View>
        )}

        {/* Approved Games */}
        {games.length === 0 && pendingSubmissions.length === 0 ? (
          <View style={styles.emptyCard}>
            <Ionicons name="game-controller-outline" size={40} color="#444" />
            <Text style={styles.emptyCardTitle}>No Games Yet</Text>
            <Text style={styles.emptyCardText}>Submit your first game to start selling on CritCash.</Text>
          </View>
        ) : (
          games.map((game) => renderGameCard(game))
        )}
      </View>
    );
  }

  function renderSubmissionCard(sub) {
    const statusColors = {
      submitted: '#f39c12',
      in_review: '#3498db',
      rejected: '#e74c3c',
    };
    const statusColor = statusColors[sub.status] || '#666';

    return (
      <View key={sub.id} style={[styles.gameCard, { borderLeftWidth: 3, borderLeftColor: statusColor }]}>
        <View style={styles.gameCardHeader}>
          <View style={[styles.gameCardIcon, { backgroundColor: statusColor + '22' }]}>
            <Ionicons name="time-outline" size={24} color={statusColor} />
          </View>
          <View style={styles.gameCardHeaderText}>
            <Text style={styles.gameCardName}>{sub.name}</Text>
            <Text style={styles.gameCardGenre}>
              {SHOW_COMPETITION_INTEGRATION
                ? `Level ${sub.integrationLevel || 0} - ${INTEGRATION_LEVEL_INFO[sub.integrationLevel || 0]?.name}`
                : (sub.genre || 'Game')}
            </Text>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: statusColor + '22' }]}>
            <Text style={[styles.statusBadgeText, { color: statusColor }]}>
              {(sub.status || 'submitted').toUpperCase().replace('_', ' ')}
            </Text>
          </View>
        </View>

        {sub.status === 'rejected' && sub.rejectionReason && (
          <View style={{ backgroundColor: '#e74c3c15', borderRadius: 8, padding: 12, marginTop: 8 }}>
            <Text style={{ color: '#e74c3c', fontWeight: '600', fontSize: 12, marginBottom: 4 }}>
              Feedback from review:
            </Text>
            <Text style={{ color: '#ccc', fontSize: 13 }}>{sub.rejectionReason}</Text>
            <TouchableOpacity
              style={[styles.addButton, { marginTop: 10, alignSelf: 'flex-start' }]}
              onPress={async () => {
                try {
                  const updateSub = httpsCallable(functions, 'updateGameSubmission');
                  await updateSub({ submissionId: sub.id, updates: {}, resubmit: true });
                  showError('Resubmitted', 'Your game has been resubmitted for review.');
                  await loadGames(selectedAccount.id);
                } catch (err) {
                  showError('Error', 'Failed to resubmit.');
                }
              }}
            >
              <Ionicons name="refresh" size={14} color="#000" />
              <Text style={styles.addButtonText}>Resubmit</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Admin review actions */}
        {isAdmin && ['submitted', 'in_review'].includes(sub.status) && (
          <View style={{ marginTop: 12 }}>
            {sub.developerNotes ? (
              <Text style={{ color: '#999', fontSize: 12, marginBottom: 8, fontStyle: 'italic' }}>
                Dev notes: {sub.developerNotes}
              </Text>
            ) : null}
            <TextInput
              style={[styles.formInput, { marginBottom: 8 }]}
              placeholder="Rejection reason (required to reject)"
              placeholderTextColor="#666"
              value={rejectionReason}
              onChangeText={setRejectionReason}
            />
            <View style={styles.adminActionsRow}>
              <TouchableOpacity
                style={styles.approveButton}
                onPress={() => handleAdminGameAction(sub.id, 'approve')}
              >
                <Ionicons name="checkmark" size={16} color="#000" />
                <Text style={styles.approveButtonText}>Approve</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.rejectButton}
                onPress={() => {
                  if (!rejectionReason.trim()) {
                    showError('Required', 'Please provide a rejection reason.');
                    return;
                  }
                  handleAdminGameAction(sub.id, 'reject');
                }}
              >
                <Ionicons name="close" size={16} color="#e74c3c" />
                <Text style={styles.rejectButtonText}>Reject</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    );
  }

  function renderGameRow(game) {
    const levelInfo = INTEGRATION_LEVEL_INFO[game.integrationLevel] || INTEGRATION_LEVEL_INFO[0];
    return (
      <View key={game.id} style={styles.gameRow}>
        <View style={[styles.gameRowIcon, { backgroundColor: levelInfo.color + '22' }]}>
          <Ionicons name={levelInfo.icon} size={20} color={levelInfo.color} />
        </View>
        <View style={styles.gameRowContent}>
          <Text style={styles.gameRowName}>{game.name}</Text>
          <Text style={styles.gameRowMeta}>
            {SHOW_COMPETITION_INTEGRATION
              ? `Level ${game.integrationLevel} - ${levelInfo.name}`
              : (game.genre || 'Game')}
          </Text>
        </View>
        <View style={[styles.statusDot, { backgroundColor: GAME_STATUS_COLORS[game.status] || '#666' }]} />
      </View>
    );
  }

  function renderGameCard(game) {
    const levelInfo = INTEGRATION_LEVEL_INFO[game.integrationLevel] || INTEGRATION_LEVEL_INFO[0];
    const isSelected = selectedGame?.id === game.id;

    return (
      <TouchableOpacity
        key={game.id}
        style={[styles.gameCard, isSelected && styles.gameCardSelected]}
        onPress={() => setSelectedGame(isSelected ? null : game)}
      >
        <View style={styles.gameCardHeader}>
          <View style={[styles.gameCardIcon, { backgroundColor: levelInfo.color + '22' }]}>
            <Ionicons name={levelInfo.icon} size={24} color={levelInfo.color} />
          </View>
          <View style={styles.gameCardHeaderText}>
            <Text style={styles.gameCardName}>{game.name}</Text>
            <Text style={styles.gameCardGenre}>{game.genre || 'Competitive'} - {game.platform || 'PC'}</Text>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: (GAME_STATUS_COLORS[game.status] || '#666') + '22' }]}>
            <Text style={[styles.statusBadgeText, { color: GAME_STATUS_COLORS[game.status] || '#666' }]}>
              {(game.status || 'draft').toUpperCase()}
            </Text>
          </View>
        </View>

        {/* Stats Row — competition activity (hidden while integration paused) */}
        {SHOW_COMPETITION_INTEGRATION && (
          <View style={styles.gameStatsRow}>
            <View style={styles.gameStat}>
              <Text style={styles.gameStatValue}>{formatNumber(game.totalMatches || 0)}</Text>
              <Text style={styles.gameStatLabel}>Matches</Text>
            </View>
            <View style={styles.gameStat}>
              <Text style={styles.gameStatValue}>{formatNumber(game.totalPlayers || 0)}</Text>
              <Text style={styles.gameStatLabel}>Players</Text>
            </View>
            <View style={styles.gameStat}>
              <Text style={styles.gameStatValue}>{formatCents(game.totalVolumeCents || 0)}</Text>
              <Text style={styles.gameStatLabel}>Volume</Text>
            </View>
            <View style={styles.gameStat}>
              <Text style={styles.gameStatValue}>{formatCents(game.revenueEarnedCents || 0)}</Text>
              <Text style={styles.gameStatLabel}>Revenue</Text>
            </View>
          </View>
        )}

        {/* Integration Info */}
        {SHOW_COMPETITION_INTEGRATION && (
          <View style={styles.integrationRow}>
            <Ionicons name={levelInfo.icon} size={14} color={levelInfo.color} />
            <Text style={styles.integrationText}>
              Level {game.integrationLevel}: {levelInfo.name}
            </Text>
            {game.currentVersion && (
              <Text style={styles.versionText}>v{game.currentVersion}</Text>
            )}
          </View>
        )}

        {/* Expanded Details */}
        {isSelected && (
          <View style={styles.gameExpandedSection}>
            <View style={styles.expandedDivider} />

            {game.description && (
              <Text style={styles.gameDescription}>{game.description}</Text>
            )}

            <View style={styles.gameDetailGrid}>
              <View style={styles.gameDetailItem}>
                <Text style={styles.gameDetailLabel}>Game ID</Text>
                <Text style={styles.gameDetailValue}>{game.gameId || game.id}</Text>
              </View>
              <View style={styles.gameDetailItem}>
                <Text style={styles.gameDetailLabel}>API Status</Text>
                <Text style={[styles.gameDetailValue, { color: game.apiEnabled ? '#2ECC71' : '#666' }]}>
                  {game.apiEnabled ? 'Enabled' : 'Disabled'}
                </Text>
              </View>
              <View style={styles.gameDetailItem}>
                <Text style={styles.gameDetailLabel}>Vision Support</Text>
                <Text style={[styles.gameDetailValue, { color: game.visionEnabled ? '#9146FF' : '#666' }]}>
                  {game.visionEnabled ? 'Active' : 'N/A'}
                </Text>
              </View>
              <View style={styles.gameDetailItem}>
                <Text style={styles.gameDetailLabel}>Created</Text>
                <Text style={styles.gameDetailValue}>{formatDate(game.createdAt)}</Text>
              </View>
            </View>

            {/* Admin actions */}
            {isAdmin && game.status === 'review' && (
              <View style={styles.adminActionsRow}>
                <TouchableOpacity
                  style={styles.approveButton}
                  onPress={() => handleAdminGameAction(game.id, 'approve')}
                >
                  <Ionicons name="checkmark" size={16} color="#000" />
                  <Text style={styles.approveButtonText}>Approve</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.rejectButton}
                  onPress={() => handleAdminGameAction(game.id, 'reject')}
                >
                  <Ionicons name="close" size={16} color="#e74c3c" />
                  <Text style={styles.rejectButtonText}>Reject</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </TouchableOpacity>
    );
  }

  async function handleAdminGameAction(submissionId, action) {
    try {
      const reviewGame = httpsCallable(functions, 'adminReviewGame');
      const result = await reviewGame({
        submissionId,
        action,
        reason: action === 'reject' ? rejectionReason : undefined,
      });

      if (action === 'approve' && result.data?.apiKey) {
        // Show the one-time API key reveal
        setRevealedKeys({ apiKey: result.data.apiKey, secretKey: result.data.secretKey });
        setShowApiKeyRevealModal(true);
      }

      showError(
        action === 'approve' ? 'Game Approved' : 'Game Rejected',
        action === 'approve'
          ? `The game is now live. Game ID: ${result.data?.gameId || 'N/A'}`
          : 'The developer has been notified with your feedback.'
      );
      setRejectionReason('');
      await loadGames(selectedAccount.id);
    } catch (err) {
      showError('Error', err.message || 'Failed to process review.');
    }
  }

  // ============================================================================
  // TAB: ANALYTICS
  // ============================================================================
  function renderAnalyticsTab() {
    return (
      <View>
        {/* Summary Cards */}
        <View style={styles.analyticsGrid}>
          <View style={[styles.analyticsCard, { borderLeftColor: '#FCD000' }]}>
            <Text style={styles.analyticsLabel}>Competition Volume (30d)</Text>
            <Text style={styles.analyticsValue}>{formatCents(monthlyVolumeCents)}</Text>
          </View>
          <View style={[styles.analyticsCard, { borderLeftColor: '#2ECC71' }]}>
            <Text style={styles.analyticsLabel}>Revenue Earned (30d)</Text>
            <Text style={styles.analyticsValue}>{formatCents(totalRevenue)}</Text>
          </View>
          <View style={[styles.analyticsCard, { borderLeftColor: '#3498db' }]}>
            <Text style={styles.analyticsLabel}>Matches (30d)</Text>
            <Text style={styles.analyticsValue}>{formatNumber(totalMatches)}</Text>
          </View>
          <View style={[styles.analyticsCard, { borderLeftColor: '#9146FF' }]}>
            <Text style={styles.analyticsLabel}>Current Tier</Text>
            <Text style={styles.analyticsValue}>{currentTier.label} ({currentTier.devShare}%)</Text>
          </View>
        </View>

        {/* Daily Volume Chart */}
        {dailyStats.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Daily Competition Volume</Text>
            <View style={styles.chartContainer}>
              {renderBarChart(dailyStats, 'competitionVolumeCents', '#FCD000')}
            </View>
            <View style={styles.chartLegend}>
              <Text style={styles.chartLegendText}>Last {dailyStats.length} days</Text>
            </View>
          </View>
        )}

        {/* Daily Revenue Chart */}
        {dailyStats.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Daily Revenue Earned</Text>
            <View style={styles.chartContainer}>
              {renderBarChart(dailyStats, 'revenueEarnedCents', '#2ECC71')}
            </View>
          </View>
        )}

        {/* Per-Game Breakdown */}
        {games.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Revenue by Game</Text>
            {games.filter(g => g.status === 'active').map((game) => {
              const gameRevenue = game.revenueEarnedCents || 0;
              const gameVolume = game.totalVolumeCents || 0;
              const maxVolume = Math.max(...games.map(g => g.totalVolumeCents || 0), 1);
              const barWidth = Math.max((gameVolume / maxVolume) * 100, 5);

              return (
                <View key={game.id} style={styles.gameBreakdownRow}>
                  <View style={styles.gameBreakdownHeader}>
                    <Text style={styles.gameBreakdownName}>{game.name}</Text>
                    <Text style={styles.gameBreakdownRevenue}>{formatCents(gameRevenue)}</Text>
                  </View>
                  <View style={styles.barBackground}>
                    <View style={[styles.barFill, { width: barWidth + '%', backgroundColor: '#FCD000' }]} />
                  </View>
                  <Text style={styles.gameBreakdownVolume}>
                    {formatCents(gameVolume)} competition volume  /  {formatNumber(game.totalMatches || 0)} matches
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Revenue Tier Info */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Revenue Share Tiers</Text>
          <Text style={styles.cardSubtitle}>
            Your share of the platform fee increases with competition volume
          </Text>
          {REVENUE_TIERS.map((tier, idx) => {
            const isCurrentTier = currentTier.label === tier.label;
            return (
              <View key={idx} style={[styles.tierRow, isCurrentTier && styles.tierRowActive]}>
                <View style={styles.tierInfo}>
                  <Text style={[styles.tierLabel, isCurrentTier && styles.tierLabelActive]}>
                    {tier.label}
                    {isCurrentTier ? '  (Current)' : ''}
                  </Text>
                  <Text style={styles.tierRange}>
                    {tier.max === Infinity
                      ? `$${(tier.min / 1000).toFixed(0)}K+/mo`
                      : `$${(tier.min / 1000).toFixed(0)}K - $${(tier.max / 1000).toFixed(0)}K/mo`
                    }
                  </Text>
                </View>
                <Text style={[styles.tierShare, isCurrentTier && styles.tierShareActive]}>
                  {tier.devShare}%
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    );
  }

  function renderBarChart(data, field, color) {
    const maxVal = Math.max(...data.map(d => d[field] || 0), 1);
    const chartWidth = SCREEN_WIDTH - 72;
    const barWidth = Math.max(Math.floor(chartWidth / data.length) - 2, 4);

    return (
      <View style={styles.barChart}>
        <View style={styles.barChartBars}>
          {data.map((d, i) => {
            const val = d[field] || 0;
            const height = Math.max((val / maxVal) * 120, 2);
            return (
              <View key={i} style={styles.barWrapper}>
                <View
                  style={[styles.bar, { height, width: barWidth, backgroundColor: color }]}
                />
              </View>
            );
          })}
        </View>
        <View style={styles.barChartLabels}>
          <Text style={styles.barChartLabel}>{formatCents(0)}</Text>
          <Text style={styles.barChartLabel}>{formatCents(maxVal)}</Text>
        </View>
      </View>
    );
  }

  // ============================================================================
  // TAB: API & KEYS
  // ============================================================================
  function renderApiTab() {
    const apiKey = selectedAccount.apiKey || 'cc_live_xxxxxxxxxxxxxxxxxxxx';
    const webhookUrl = selectedAccount.webhookUrl || '';
    const integrationLevel = selectedAccount.integrationLevel ?? 0;
    const levelInfo = INTEGRATION_LEVEL_INFO[integrationLevel] || INTEGRATION_LEVEL_INFO[0];

    return (
      <View>
        {/* API Key Card */}
        <View style={styles.card}>
          <View style={styles.cardTitleRow}>
            <Text style={styles.cardTitle}>API Credentials</Text>
            <View style={[styles.statusBadge, { backgroundColor: '#2ECC7122' }]}>
              <Text style={[styles.statusBadgeText, { color: '#2ECC71' }]}>ACTIVE</Text>
            </View>
          </View>

          <View style={styles.apiKeyBox}>
            <Text style={styles.apiKeyLabel}>API Key</Text>
            <View style={styles.apiKeyRow}>
              <Text style={styles.apiKeyValue}>
                {apiKeyRevealed ? apiKey : apiKey.substring(0, 8) + '****************************'}
              </Text>
              <TouchableOpacity onPress={() => setApiKeyRevealed(!apiKeyRevealed)}>
                <Ionicons name={apiKeyRevealed ? 'eye-off' : 'eye'} size={20} color="#666" />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.apiKeyBox}>
            <Text style={styles.apiKeyLabel}>Developer ID</Text>
            <Text style={styles.apiKeyValue}>{selectedAccount.id}</Text>
          </View>

          {webhookUrl ? (
            <View style={styles.apiKeyBox}>
              <Text style={styles.apiKeyLabel}>Webhook URL</Text>
              <Text style={styles.apiKeyValue}>{webhookUrl}</Text>
            </View>
          ) : null}

          <View style={styles.apiActionsRow}>
            <TouchableOpacity style={styles.apiActionButton} onPress={handleRotateApiKey}>
              <Ionicons name="refresh" size={16} color="#FCD000" />
              <Text style={styles.apiActionText}>Rotate Key</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Integration Level */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Integration Level</Text>
          <View style={styles.integrationLevelCard}>
            <View style={[styles.integrationLevelIcon, { backgroundColor: levelInfo.color + '22' }]}>
              <Ionicons name={levelInfo.icon} size={32} color={levelInfo.color} />
            </View>
            <View style={styles.integrationLevelInfo}>
              <Text style={styles.integrationLevelName}>Level {integrationLevel}: {levelInfo.name}</Text>
              <Text style={styles.integrationLevelDesc}>{levelInfo.description}</Text>
            </View>
          </View>
        </View>

        {/* Available Endpoints */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Available Endpoints</Text>
          <Text style={styles.cardSubtitle}>Based on your integration level</Text>

          {renderEndpoint('POST', '/api/v1/auth/game-token', 'Exchange user auth for game session', 1)}
          {renderEndpoint('GET', '/api/v1/auth/validate', 'Validate token and get user info', 1)}
          {renderEndpoint('POST', '/api/v1/match/result', 'Report match outcome', 1)}
          {renderEndpoint('POST', '/api/v1/match/create', 'Initialize match, reserve entry Crits', 2)}
          {renderEndpoint('POST', '/api/v1/match/start', 'Mark match as started', 2)}
          {renderEndpoint('POST', '/api/v1/match/cancel', 'Cancel match, return entry Crits', 2)}
          {renderEndpoint('POST', '/api/v1/tournament/create', 'Create tournament bracket', 2)}
          {renderEndpoint('POST', '/api/v1/tournament/register', 'Register player for tournament', 2)}
          {renderEndpoint('GET', '/api/v1/wallet/balance', 'Get user Crit balance', 3)}
          {renderEndpoint('POST', '/api/v1/wallet/hold', 'Place hold on entry Crits', 3)}
          {renderEndpoint('POST', '/api/v1/wallet/release', 'Release held Crits to player', 3)}
        </View>

        {/* Webhook Events */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Webhook Events</Text>
          <Text style={styles.cardSubtitle}>Subscribe to real-time competition events</Text>

          {renderWebhookEvent('match.created', 'New match initialized')}
          {renderWebhookEvent('match.ready', 'All players confirmed')}
          {renderWebhookEvent('match.started', 'Match in progress')}
          {renderWebhookEvent('match.completed', 'Result confirmed, distribution processed')}
          {renderWebhookEvent('match.disputed', 'Result contested by player')}
          {renderWebhookEvent('tournament.round_complete', 'Bracket round advanced')}
        </View>

        {/* Documentation Link */}
        <TouchableOpacity
          style={styles.docsLinkCard}
          onPress={() => {
            try {
              const { Linking } = require('react-native');
              Linking.openURL('https://critcash.com/api.info');
            } catch (e) {}
          }}
        >
          <Ionicons name="document-text" size={24} color="#FCD000" />
          <View style={{ flex: 1 }}>
            <Text style={styles.docsLinkTitle}>Full API Documentation</Text>
            <Text style={styles.docsLinkText}>docs.critcash.com/api</Text>
          </View>
          <Ionicons name="open-outline" size={20} color="#666" />
        </TouchableOpacity>
      </View>
    );
  }

  function renderEndpoint(method, path, description, requiredLevel) {
    const integrationLevel = selectedAccount.integrationLevel ?? 0;
    const available = integrationLevel >= requiredLevel;
    return (
      <View key={path} style={[styles.endpointRow, !available && styles.endpointRowDisabled]}>
        <View style={[styles.methodBadge, { backgroundColor: method === 'POST' ? '#3498db22' : '#2ECC7122' }]}>
          <Text style={[styles.methodText, { color: method === 'POST' ? '#3498db' : '#2ECC71' }]}>{method}</Text>
        </View>
        <View style={styles.endpointInfo}>
          <Text style={[styles.endpointPath, !available && { color: '#444' }]}>{path}</Text>
          <Text style={styles.endpointDesc}>{description}</Text>
        </View>
        {!available && (
          <Text style={styles.endpointLock}>L{requiredLevel}+</Text>
        )}
      </View>
    );
  }

  function renderWebhookEvent(event, description) {
    return (
      <View key={event} style={styles.webhookRow}>
        <Ionicons name="radio-button-on" size={12} color="#FCD000" />
        <View style={styles.webhookInfo}>
          <Text style={styles.webhookEvent}>{event}</Text>
          <Text style={styles.webhookDesc}>{description}</Text>
        </View>
      </View>
    );
  }

  // ============================================================================
  // ADMIN: PENDING REVIEWS
  // ============================================================================
  function renderAdminPendingSection() {
    const pendingGames = pendingSubmissions.filter(s =>
      ['submitted', 'in_review'].includes(s.status)
    );
    const totalPending = developerApplications.length + pendingGames.length;

    if (totalPending === 0) return null;

    return (
      <View>
        {/* Developer Applications */}
        {developerApplications.length > 0 && (
          <View style={styles.card}>
            <View style={styles.cardTitleRow}>
              <Text style={styles.cardTitle}>Developer Applications ({developerApplications.length})</Text>
              <View style={styles.adminBadge}>
                <Text style={styles.adminBadgeText}>ADMIN</Text>
              </View>
            </View>
            {developerApplications.map((app) => (
              <View key={app.id} style={[styles.pendingItem, { flexDirection: 'column', alignItems: 'stretch', paddingVertical: 12 }]}>
                {/* App Header */}
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#f39c1222', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
                    <Ionicons name="person-add" size={18} color="#f39c12" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: '#fff', fontWeight: '700', fontSize: 15 }}>{app.studioName}</Text>
                    <Text style={{ color: '#999', fontSize: 12 }}>{app.email}</Text>
                  </View>
                  <View style={[styles.statusBadge, { backgroundColor: '#f39c1222' }]}>
                    <Text style={[styles.statusBadgeText, { color: '#f39c12' }]}>NEW</Text>
                  </View>
                </View>

                {/* App Details */}
                <View style={{ backgroundColor: '#111', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  {app.gameName ? (
                    <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                      <Text style={{ color: '#666', fontSize: 12, width: 90 }}>Game</Text>
                      <Text style={{ color: '#ccc', fontSize: 12, flex: 1 }}>{app.gameName}</Text>
                    </View>
                  ) : null}
                  <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                    <Text style={{ color: '#666', fontSize: 12, width: 90 }}>Integration</Text>
                    <Text style={{ color: '#ccc', fontSize: 12, flex: 1 }}>
                      Level {app.integrationLevel ?? 0} - {INTEGRATION_LEVEL_INFO[app.integrationLevel ?? 0]?.name}
                    </Text>
                  </View>
                  {app.website ? (
                    <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                      <Text style={{ color: '#666', fontSize: 12, width: 90 }}>Website</Text>
                      <Text style={{ color: '#3498db', fontSize: 12, flex: 1 }}>{app.website}</Text>
                    </View>
                  ) : null}
                  {app.teamSize ? (
                    <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                      <Text style={{ color: '#666', fontSize: 12, width: 90 }}>Team Size</Text>
                      <Text style={{ color: '#ccc', fontSize: 12, flex: 1 }}>{app.teamSize}</Text>
                    </View>
                  ) : null}
                  {app.description ? (
                    <View style={{ marginTop: 4 }}>
                      <Text style={{ color: '#666', fontSize: 12, marginBottom: 2 }}>Description</Text>
                      <Text style={{ color: '#ccc', fontSize: 12 }}>{app.description}</Text>
                    </View>
                  ) : null}
                  {!app.userId && (
                    <View style={{ marginTop: 6, backgroundColor: '#e74c3c22', borderRadius: 4, padding: 6 }}>
                      <Text style={{ color: '#e74c3c', fontSize: 11 }}>
                        No CritCash account linked — applicant will need to sign up first
                      </Text>
                    </View>
                  )}
                </View>

                {/* Rejection reason input */}
                <TextInput
                  style={[styles.formInput, { marginBottom: 8, fontSize: 13 }]}
                  placeholder="Rejection reason (only needed to reject)"
                  placeholderTextColor="#555"
                  value={activeRejectAppId === app.id ? rejectionReason : ''}
                  onChangeText={(text) => {
                    setActiveRejectAppId(app.id);
                    setRejectionReason(text);
                  }}
                  onFocus={() => setActiveRejectAppId(app.id)}
                />

                {/* Actions */}
                <View style={styles.adminActionsRow}>
                  <TouchableOpacity
                    style={[styles.approveButton, processingAppId === app.id && { opacity: 0.6 }]}
                    onPress={() => handleApproveApplication(app)}
                    disabled={processingAppId === app.id}
                  >
                    {processingAppId === app.id ? (
                      <ActivityIndicator size="small" color="#000" />
                    ) : (
                      <>
                        <Ionicons name="checkmark" size={16} color="#000" />
                        <Text style={styles.approveButtonText}>Create Account</Text>
                      </>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.rejectButton}
                    onPress={() => handleRejectApplication(app)}
                    disabled={processingAppId === app.id}
                  >
                    <Ionicons name="close" size={16} color="#e74c3c" />
                    <Text style={styles.rejectButtonText}>Reject</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Pending Game Submissions */}
        {pendingGames.length > 0 && (
          <View style={styles.card}>
            <View style={styles.cardTitleRow}>
              <Text style={styles.cardTitle}>Pending Game Reviews ({pendingGames.length})</Text>
              <View style={styles.adminBadge}>
                <Text style={styles.adminBadgeText}>ADMIN</Text>
              </View>
            </View>
            {pendingGames.map((sub) => (
              <View key={sub.id} style={styles.pendingItem}>
                <View style={styles.pendingItemInfo}>
                  <Ionicons name="game-controller" size={16} color="#f39c12" />
                  <Text style={styles.pendingItemName}>{sub.name}</Text>
                  <Text style={styles.pendingItemType}>Game Review</Text>
                </View>
                <View style={styles.pendingItemActions}>
                  <TouchableOpacity
                    style={styles.approveButtonSmall}
                    onPress={() => handleAdminGameAction(sub.id, 'approve')}
                  >
                    <Ionicons name="checkmark" size={14} color="#000" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.rejectButtonSmall}
                    onPress={() => handleAdminGameAction(sub.id, 'reject')}
                  >
                    <Ionicons name="close" size={14} color="#e74c3c" />
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  }

  // ============================================================================
  // MODALS
  // ============================================================================
  function renderAccountSwitcherModal() {
    return (
      <Modal visible={showAccountSwitcher} transparent animationType="fade" onRequestClose={() => setShowAccountSwitcher(false)}>
        <TouchableOpacity style={styles.modalBackdrop} activeOpacity={1} onPress={() => setShowAccountSwitcher(false)}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Switch Developer Account</Text>
            {accounts.map((acct) => (
              <TouchableOpacity
                key={acct.id}
                style={[styles.accountOption, selectedAccount?.id === acct.id && styles.accountOptionSelected]}
                onPress={() => handleSwitchAccount(acct)}
              >
                <View style={styles.accountOptionIcon}>
                  <Ionicons name="code-slash" size={20} color="#FCD000" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.accountOptionName}>{acct.studioName}</Text>
                  <Text style={styles.accountOptionEmail}>{acct.contactEmail}</Text>
                </View>
                {selectedAccount?.id === acct.id && (
                  <Ionicons name="checkmark-circle" size={20} color="#FCD000" />
                )}
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowAccountSwitcher(false)}>
              <Text style={styles.modalCloseText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    );
  }

  function renderNewGameModal() {
    return (
      <Modal visible={showNewGameModal} transparent animationType="fade" onRequestClose={() => setShowNewGameModal(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainerLarge}>
            <ScrollView contentContainerStyle={styles.modalScrollContent}>
              <View style={styles.modalHeader}>
                <Ionicons name="game-controller" size={40} color="#FCD000" />
                <Text style={styles.modalTitle}>Submit a Game</Text>
                <Text style={styles.modalSubtitle}>Submit your game for review on the CritCash platform</Text>
              </View>

              {/* Game Name */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Game Name *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Enter game name"
                  placeholderTextColor="#666"
                  value={newGameForm.name}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, name: t })}
                />
              </View>

              {/* Description */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Description</Text>
                <TextInput
                  style={[styles.formInput, { height: 80, textAlignVertical: 'top' }]}
                  placeholder="Brief description of your game"
                  placeholderTextColor="#666"
                  multiline
                  value={newGameForm.description}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, description: t })}
                />
              </View>

              {/* Genres and Platforms */}
              <View style={{ flexDirection: SCREEN_WIDTH > 600 ? 'row' : 'column', gap: 12 }}>
                <View style={[styles.formGroup, { flex: 1 }]}>
                  <Text style={styles.formLabel}>Genres</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder="e.g., Fighting, FPS, Racing"
                    placeholderTextColor="#666"
                    value={newGameForm.genres}
                    onChangeText={(t) => setNewGameForm({ ...newGameForm, genres: t })}
                  />
                </View>
                <View style={[styles.formGroup, { flex: 1 }]}>
                  <Text style={styles.formLabel}>Platforms</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder="e.g., Windows, macOS, Linux"
                    placeholderTextColor="#666"
                    value={newGameForm.platforms}
                    onChangeText={(t) => setNewGameForm({ ...newGameForm, platforms: t })}
                  />
                </View>
              </View>

              {/* Media */}
              <View style={styles.formGroup}>
                <Text style={[styles.formLabel, { marginBottom: 2 }]}>Media</Text>
                <Text style={{ color: '#666', fontSize: 11, marginBottom: 8 }}>Cover art, trailer, and store links</Text>
                <TextInput
                  style={[styles.formInput, { marginBottom: 8 }]}
                  placeholder="Cover image URL"
                  placeholderTextColor="#666"
                  value={newGameForm.coverImageUrl}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, coverImageUrl: t })}
                />
                <TextInput
                  style={[styles.formInput, { marginBottom: 8 }]}
                  placeholder="Trailer URL (YouTube, etc.)"
                  placeholderTextColor="#666"
                  value={newGameForm.trailerUrl}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, trailerUrl: t })}
                />
                <TextInput
                  style={styles.formInput}
                  placeholder="Store page URL (Steam, itch.io, etc.)"
                  placeholderTextColor="#666"
                  value={newGameForm.externalStoreUrl}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, externalStoreUrl: t })}
                />
              </View>

              {/* Downloads */}
              <View style={styles.formGroup}>
                <Text style={[styles.formLabel, { marginBottom: 2 }]}>Download URLs</Text>
                <Text style={{ color: '#666', fontSize: 11, marginBottom: 8 }}>Direct download links per platform (optional)</Text>
                <TextInput
                  style={[styles.formInput, { marginBottom: 8 }]}
                  placeholder="Windows download URL"
                  placeholderTextColor="#666"
                  value={newGameForm.downloadUrlWin}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, downloadUrlWin: t })}
                />
                <TextInput
                  style={styles.formInput}
                  placeholder="macOS download URL"
                  placeholderTextColor="#666"
                  value={newGameForm.downloadUrlMac}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, downloadUrlMac: t })}
                />
              </View>

              {/* Integration Level + Competition Config — hidden while the
                  3rd-party competition-integration path is paused. Defaults in
                  newGameForm (level 0, h2h) still submit so existing backend
                  validation is satisfied. */}
              {SHOW_COMPETITION_INTEGRATION && (
                <>
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>Integration Level</Text>
                    <View style={styles.levelPicker}>
                      {Object.entries(INTEGRATION_LEVEL_INFO).map(([lvl, info]) => {
                        const level = parseInt(lvl);
                        const active = newGameForm.integrationLevel === level;
                        return (
                          <TouchableOpacity
                            key={lvl}
                            style={[styles.levelPickerOption, active && styles.levelPickerOptionActive]}
                            onPress={() => setNewGameForm({ ...newGameForm, integrationLevel: level })}
                          >
                            <Ionicons name={info.icon} size={16} color={active ? '#000' : info.color} />
                            <Text style={[styles.levelPickerText, active && styles.levelPickerTextActive]}>
                              L{lvl}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                    <Text style={{ color: '#999', fontSize: 11, marginTop: 4 }}>
                      {INTEGRATION_LEVEL_INFO[newGameForm.integrationLevel]?.description}
                    </Text>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={[styles.formLabel, { marginBottom: 2 }]}>Competition Settings</Text>
                    <Text style={{ color: '#666', fontSize: 11, marginBottom: 8 }}>How players will compete</Text>
                    <TextInput
                      style={[styles.formInput, { marginBottom: 8 }]}
                      placeholder="Modes (e.g., h2h, bracket)"
                      placeholderTextColor="#666"
                      value={newGameForm.competitionModes}
                      onChangeText={(t) => setNewGameForm({ ...newGameForm, competitionModes: t })}
                    />
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <View style={{ flex: 1 }}>
                        <TextInput
                          style={styles.formInput}
                          placeholder="Min players"
                          placeholderTextColor="#666"
                          keyboardType="numeric"
                          value={newGameForm.minPlayers}
                          onChangeText={(t) => setNewGameForm({ ...newGameForm, minPlayers: t })}
                        />
                      </View>
                      <View style={{ flex: 1 }}>
                        <TextInput
                          style={styles.formInput}
                          placeholder="Max players"
                          placeholderTextColor="#666"
                          keyboardType="numeric"
                          value={newGameForm.maxPlayers}
                          onChangeText={(t) => setNewGameForm({ ...newGameForm, maxPlayers: t })}
                        />
                      </View>
                    </View>
                    <TouchableOpacity
                      style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}
                      onPress={() => setNewGameForm({ ...newGameForm, streamRequired: !newGameForm.streamRequired })}
                    >
                      <Ionicons
                        name={newGameForm.streamRequired ? 'checkbox' : 'square-outline'}
                        size={20}
                        color={newGameForm.streamRequired ? '#FCD000' : '#666'}
                      />
                      <Text style={{ color: '#ccc', fontSize: 13, marginLeft: 8 }}>Stream required for match verification</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}

              {/* Developer Notes */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Notes for Reviewer</Text>
                <TextInput
                  style={[styles.formInput, { height: 60, textAlignVertical: 'top' }]}
                  placeholder="Anything our team should know about your game..."
                  placeholderTextColor="#666"
                  multiline
                  value={newGameForm.developerNotes}
                  onChangeText={(t) => setNewGameForm({ ...newGameForm, developerNotes: t })}
                />
              </View>

              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.modalButtonCancel} onPress={() => setShowNewGameModal(false)}>
                  <Text style={styles.modalButtonCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalButtonSubmit, submitting && { backgroundColor: '#666' }]}
                  onPress={handleRegisterGame}
                  disabled={submitting}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <Text style={styles.modalButtonSubmitText}>Submit for Review</Text>
                  )}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  }

  function renderApiKeyRevealModal() {
    if (!revealedKeys) return null;
    return (
      <Modal visible={showApiKeyRevealModal} transparent animationType="fade" onRequestClose={() => setShowApiKeyRevealModal(false)}>
        <View style={styles.modalBackdrop}>
          <View style={[styles.modalContainerLarge, { maxWidth: 500 }]}>
            <View style={styles.modalScrollContent}>
              <View style={styles.modalHeader}>
                <Ionicons name="key" size={40} color="#FCD000" />
                <Text style={styles.modalTitle}>API Credentials</Text>
                <Text style={[styles.modalSubtitle, { color: '#e74c3c' }]}>
                  Save these now - the secret key will NOT be shown again
                </Text>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>API Key</Text>
                <View style={[styles.formInput, { flexDirection: 'row', alignItems: 'center' }]}>
                  <Text style={{ color: '#2ECC71', flex: 1, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }} selectable>
                    {revealedKeys.apiKey}
                  </Text>
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Secret Key</Text>
                <View style={[styles.formInput, { flexDirection: 'row', alignItems: 'center' }]}>
                  <Text style={{ color: '#FCD000', flex: 1, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }} selectable>
                    {revealedKeys.secretKey}
                  </Text>
                </View>
              </View>

              <View style={{ backgroundColor: '#e74c3c22', borderRadius: 8, padding: 12, marginTop: 8 }}>
                <Text style={{ color: '#e74c3c', fontSize: 12, textAlign: 'center' }}>
                  Copy and store the secret key securely. Once you close this dialog, only the API key will be visible.
                </Text>
              </View>

              <View style={[styles.modalActions, { marginTop: 16 }]}>
                <TouchableOpacity
                  style={styles.modalButtonSubmit}
                  onPress={() => {
                    setShowApiKeyRevealModal(false);
                    setRevealedKeys(null);
                  }}
                >
                  <Text style={styles.modalButtonSubmitText}>I've Saved My Keys</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  }
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  loadingText: {
    color: '#999',
    marginTop: 12,
    fontSize: 14,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
    maxWidth: 300,
    marginBottom: 24,
    lineHeight: 20,
  },
  goldButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 8,
  },
  goldButtonText: {
    color: '#000',
    fontSize: 15,
    fontWeight: 'bold',
  },

  // Dashboard Header
  dashboardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  dashboardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  studioIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#1A1A1A',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#FCD00044',
  },
  dashboardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dashboardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFF',
  },
  dashboardSubtitle: {
    fontSize: 13,
    color: '#888',
    marginTop: 2,
  },
  adminBadge: {
    backgroundColor: '#FCD00022',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#FCD00044',
  },
  adminBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#FCD000',
  },

  // Tabs
  tabBar: {
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  tabScroll: {
    paddingHorizontal: 16,
  },
  tab: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 4,
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: '#FCD000',
  },
  tabText: {
    fontSize: 14,
    color: '#888',
    fontWeight: '500',
  },
  tabTextActive: {
    color: '#FCD000',
    fontWeight: '600',
  },

  // Scroll
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
  },

  // KPI Grid
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
  },
  kpiCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#111',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: '#1A1A1A',
  },
  kpiValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 8,
  },
  kpiLabel: {
    fontSize: 12,
    color: '#888',
    marginTop: 4,
  },

  // Cards
  card: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#1A1A1A',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 12,
  },
  cardTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  cardSubtitle: {
    fontSize: 13,
    color: '#888',
    marginBottom: 16,
    marginTop: -8,
  },

  // Detail Rows
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  detailLabel: {
    fontSize: 13,
    color: '#888',
  },
  detailValue: {
    fontSize: 13,
    color: '#FFF',
    fontWeight: '500',
  },

  // Status Badge
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
  },

  // Quick Actions
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  quickAction: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#0A0A0A',
    borderRadius: 8,
    padding: 14,
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#1A1A1A',
  },
  quickActionText: {
    fontSize: 12,
    color: '#CCC',
    fontWeight: '500',
  },

  // Game Row (Overview)
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
    gap: 12,
  },
  gameRowIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  gameRowContent: {
    flex: 1,
  },
  gameRowName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFF',
  },
  gameRowMeta: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },

  // See All
  seeAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 12,
    gap: 6,
  },
  seeAllText: {
    fontSize: 13,
    color: '#FCD000',
    fontWeight: '500',
  },

  // Tab Header Row
  tabHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  tabHeaderTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFF',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FCD000',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    gap: 6,
  },
  addButtonText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#000',
  },

  // Empty Card
  emptyCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 32,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#1A1A1A',
  },
  emptyCardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 12,
  },
  emptyCardText: {
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
    marginTop: 8,
  },

  // Game Card (Games Tab)
  gameCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#1A1A1A',
  },
  gameCardSelected: {
    borderColor: '#FCD00044',
  },
  gameCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  gameCardIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  gameCardHeaderText: {
    flex: 1,
  },
  gameCardName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFF',
  },
  gameCardGenre: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },

  // Game Stats Row
  gameStatsRow: {
    flexDirection: 'row',
    backgroundColor: '#0A0A0A',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  gameStat: {
    flex: 1,
    alignItems: 'center',
  },
  gameStatValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFF',
  },
  gameStatLabel: {
    fontSize: 10,
    color: '#888',
    marginTop: 2,
  },

  // Integration Row
  integrationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  integrationText: {
    fontSize: 12,
    color: '#888',
  },
  versionText: {
    fontSize: 11,
    color: '#666',
    marginLeft: 'auto',
    backgroundColor: '#1A1A1A',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },

  // Game Expanded
  gameExpandedSection: {
    marginTop: 4,
  },
  expandedDivider: {
    height: 1,
    backgroundColor: '#1A1A1A',
    marginVertical: 12,
  },
  gameDescription: {
    fontSize: 13,
    color: '#999',
    lineHeight: 18,
    marginBottom: 12,
  },
  gameDetailGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  gameDetailItem: {
    flex: 1,
    minWidth: '45%',
  },
  gameDetailLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 2,
  },
  gameDetailValue: {
    fontSize: 13,
    color: '#FFF',
    fontWeight: '500',
  },

  // Admin Actions
  adminActionsRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
  },
  approveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FCD000',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    gap: 6,
  },
  approveButtonText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#000',
  },
  rejectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A1A1A',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    gap: 6,
    borderWidth: 1,
    borderColor: '#e74c3c44',
  },
  rejectButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#e74c3c',
  },

  // Analytics
  analyticsGrid: {
    gap: 10,
    marginBottom: 16,
  },
  analyticsCard: {
    backgroundColor: '#111',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: '#1A1A1A',
    borderLeftWidth: 3,
  },
  analyticsLabel: {
    fontSize: 12,
    color: '#888',
  },
  analyticsValue: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 4,
  },

  // Bar Chart
  chartContainer: {
    marginTop: 8,
  },
  barChart: {},
  barChartBars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 130,
    gap: 2,
    paddingBottom: 4,
  },
  barWrapper: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  bar: {
    borderRadius: 2,
    minHeight: 2,
  },
  barChartLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 6,
  },
  barChartLabel: {
    fontSize: 10,
    color: '#666',
  },
  chartLegend: {
    alignItems: 'center',
    marginTop: 8,
  },
  chartLegendText: {
    fontSize: 11,
    color: '#666',
  },

  // Game Breakdown
  gameBreakdownRow: {
    marginBottom: 16,
  },
  gameBreakdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  gameBreakdownName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFF',
  },
  gameBreakdownRevenue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2ECC71',
  },
  barBackground: {
    height: 8,
    backgroundColor: '#1A1A1A',
    borderRadius: 4,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 4,
  },
  gameBreakdownVolume: {
    fontSize: 11,
    color: '#666',
    marginTop: 4,
  },

  // Revenue Tiers
  tierRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  tierRowActive: {
    backgroundColor: '#FCD00008',
    marginHorizontal: -16,
    paddingHorizontal: 16,
    borderLeftWidth: 3,
    borderLeftColor: '#FCD000',
  },
  tierInfo: {},
  tierLabel: {
    fontSize: 14,
    color: '#FFF',
    fontWeight: '500',
  },
  tierLabelActive: {
    color: '#FCD000',
    fontWeight: 'bold',
  },
  tierRange: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  tierShare: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#888',
  },
  tierShareActive: {
    color: '#FCD000',
  },

  // API Tab
  apiKeyBox: {
    backgroundColor: '#0A0A0A',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  apiKeyLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  apiKeyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  apiKeyValue: {
    fontSize: 13,
    color: '#FFF',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    flex: 1,
  },
  apiActionsRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 6,
  },
  apiActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCD00044',
  },
  apiActionText: {
    fontSize: 13,
    color: '#FCD000',
    fontWeight: '500',
  },

  // Integration Level
  integrationLevelCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    backgroundColor: '#0A0A0A',
    borderRadius: 10,
    padding: 16,
  },
  integrationLevelIcon: {
    width: 56,
    height: 56,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  integrationLevelInfo: {
    flex: 1,
  },
  integrationLevelName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FFF',
  },
  integrationLevelDesc: {
    fontSize: 12,
    color: '#888',
    marginTop: 4,
  },

  // Endpoints
  endpointRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  endpointRowDisabled: {
    opacity: 0.4,
  },
  methodBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    minWidth: 44,
    alignItems: 'center',
  },
  methodText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  endpointInfo: {
    flex: 1,
  },
  endpointPath: {
    fontSize: 12,
    color: '#FFF',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  endpointDesc: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  endpointLock: {
    fontSize: 10,
    color: '#666',
    fontWeight: 'bold',
  },

  // Webhooks
  webhookRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  webhookInfo: {
    flex: 1,
  },
  webhookEvent: {
    fontSize: 13,
    color: '#FFF',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  webhookDesc: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },

  // Docs Link
  docsLinkCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    gap: 14,
    borderWidth: 1,
    borderColor: '#FCD00033',
    marginBottom: 16,
  },
  docsLinkTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FFF',
  },
  docsLinkText: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },

  // Pending Items (Admin)
  pendingItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  pendingItemInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  pendingItemName: {
    fontSize: 14,
    color: '#FFF',
    fontWeight: '500',
  },
  pendingItemType: {
    fontSize: 11,
    color: '#888',
  },
  pendingItemActions: {
    flexDirection: 'row',
    gap: 8,
  },
  approveButtonSmall: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#FCD000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rejectButtonSmall: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#1A1A1A',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e74c3c44',
  },

  // Modals
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContainer: {
    backgroundColor: '#111',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    padding: 24,
  },
  modalContainerLarge: {
    backgroundColor: '#111',
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalScrollContent: {
    padding: 24,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 12,
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 13,
    color: '#888',
  },
  modalCloseButton: {
    backgroundColor: '#1A1A1A',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  modalCloseText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '500',
  },

  // Account Options
  accountOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 12,
    marginBottom: 8,
    backgroundColor: '#0A0A0A',
  },
  accountOptionSelected: {
    borderWidth: 1,
    borderColor: '#FCD00044',
  },
  accountOptionIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#1A1A1A',
    justifyContent: 'center',
    alignItems: 'center',
  },
  accountOptionName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFF',
  },
  accountOptionEmail: {
    fontSize: 12,
    color: '#888',
  },

  // Form
  formGroup: {
    marginBottom: 14,
  },
  formLabel: {
    fontSize: 12,
    color: '#999',
    marginBottom: 6,
    fontWeight: '600',
  },
  formInput: {
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#FFF',
    borderWidth: 1,
    borderColor: '#333',
  },
  levelPicker: {
    flexDirection: 'row',
    gap: 8,
  },
  levelPickerOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#1A1A1A',
    borderWidth: 1,
    borderColor: '#333',
  },
  levelPickerOptionActive: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  levelPickerText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#FFF',
  },
  levelPickerTextActive: {
    color: '#000',
  },

  // Modal Actions
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  modalButtonCancel: {
    flex: 1,
    backgroundColor: '#1A1A1A',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonCancelText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  modalButtonSubmit: {
    flex: 2,
    backgroundColor: '#FCD000',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonSubmitText: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },
});